import { type FC, type ReactNode, memo } from 'react';
import { Link } from 'react-router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ChevronRight,
  ExpandMore,
  Tag,
  type SvgIconComponent,
} from '@mui/icons-material';

import { type AuditMechanicalFormEntry } from '#packages/data-collection/models/audit-mechanical-form';

export type AuditFormMechanicalPageRendererProps = {
  systems: Array<{
    id: string;
    label: ReactNode;
    Icon: SvgIconComponent;
    entriesLinkMap: Record<string, AuditMechanicalFormEntry>;
  }>;
};

export const AuditFormMechanicalPageRenderer: FC<AuditFormMechanicalPageRendererProps> =
  memo(({ systems }) => (
    <Box flexGrow={1}>
      {systems.map((system) => (
        <Accordion key={system.id} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <system.Icon fontSize="small" />
              <Typography variant="body1">{system.label}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack>
              {Object.entries(system.entriesLinkMap).map(
                ([entryPageLink, entry], index) => (
                  <ButtonBase
                    key={entryPageLink}
                    component={Link}
                    to={entryPageLink}
                    sx={{ display: 'block', textAlign: 'start' }}>
                    <Grid container alignItems="center" sx={{ p: 2 }}>
                      <Grid size="grow">
                        <Typography
                          component="span"
                          variant="body2"
                          fontWeight="bold">
                          {entry.type}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textSecondary">
                          &nbsp;•&nbsp;
                        </Typography>
                        <Typography component="span" variant="body2">
                          {entry.location}
                        </Typography>
                      </Grid>
                      <Grid>
                        {entry.tag != null && (
                          <Chip
                            icon={<Tag fontSize="small" />}
                            label={entry.tag.toUpperCase()}
                            variant="outlined"
                          />
                        )}
                      </Grid>
                      <Grid>
                        <ChevronRight
                          sx={(theme) => ({ color: theme.palette.grey[400] })}
                        />
                      </Grid>
                    </Grid>
                    {index < Object.keys(system.entriesLinkMap).length - 1 && (
                      <Divider />
                    )}
                  </ButtonBase>
                ),
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  ));
